import { useState, useEffect, useRef } from "react";
import {
  Card,
  Upload,
  Button,
  List,
  Progress,
  message,
  Steps,
  Space,
  Input,
  Row,
  Col,
  Grid,
  Tooltip,
  Modal,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  LoadingOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useFileUpload } from "../utils/httpBase";
import { connectWebSocket } from "../utils/websocket";
import { useNavigate } from "react-router-dom";
import { checkBalance } from "../../../helpers/checkBalance";
import { useAppSelector } from "../../../hooks/reduxHooks";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
import PdfViewer from "../../../components/pdf-viewer";
import { Typography } from "antd";

const { Text } = Typography;

const { Dragger } = Upload;
const { Step } = Steps;

function FileUploadPanel({
  setOnUploadComplete,
  setActiveTab,
  fetchExistingFiles,
  page,
  onClose,
  fetchSources,
  fetchTOC,
}) {
  const [fileList, setFileList] = useState([]);
  const [urls, setUrls] = useState([]);
  const [currentUrl, setCurrentUrl] = useState("");
  const [status, setStatus] = useState({
    currentStep: 0,
    stepStatuses: {},
  });
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { startProcessing } = useFileUpload(setStatus);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("Uploading more than 2 documents");
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [previewPdfTitle, setPreviewPdfTitle] = useState("");
  const previewObjectUrlRef = useRef("");

  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);
  const { totalFiles } = useAppSelector((state) => state.documentCount);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const rowGutter = isMobile ? [16, 16] : 16;
  const draggerStyle = {
    padding: isMobile ? "12px" : "20px",
    border: "2px dashed #d9d9d9",
    borderRadius: "8px",
    background: "#fafafa",
    transition: "border-color 0.3s",
    flex: isMobile ? "none" : 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: isMobile ? "140px" : "200px",
  };

  const hasSelections = fileList.length > 0 || urls.length > 0;

  useEffect(() => {
    setCompleted(false);
    if (processing) {
      const socket = connectWebSocket((newStatus) => {
        setStatus((prevStatus) => ({
          ...prevStatus,
          stepStatuses: newStatus,
        }));
      });
      return () => {
        socket.close();
      };
    }
  }, [processing]);

  useEffect(() => {
    const completedSteps = Object.entries(status.stepStatuses).filter(
      ([_, value]) => value === "Completed"
    );
    setStatus((prevStatus) => ({
      ...prevStatus,
      currentStep: completedSteps.length,
    }));
  }, [status.stepStatuses]);

  useEffect(() => {
    const steps = Object.keys(status.stepStatuses);
    const isComplete =
      steps.length > 0 &&
      Object.values(status.stepStatuses).every(
        (stepStatus) => stepStatus === "Completed"
      );
    if (isComplete) {
      fetchSources();
      fetchTOC();
      setFileList([]);
      setUrls([]);
      fetchExistingFiles(page);
      setActiveTab("2");
      setProcessing(false);
      setCompleted(true);
      setStatus({ currentStep: 0, stepStatuses: {} });
    }
  }, [status.stepStatuses, page]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = "";
      }
    };
  }, []);

  const handleBeforeUpload = (file) => {
    // Check if user is on free plan and trying to select more than allowed
    if (!isPaidPlan) {
      const remaining = Math.max(0, 2 - totalFiles);
      const totalSelections = fileList.length + urls.length;
      if (totalSelections >= remaining) {
        setUpgradeFeature("Uploading more than 2 documents");
        setShowUpgradeDialog(true);
        return Upload.LIST_IGNORE;
      }
    }

    // Check if user is on free plan and already has 2 or more documents
    if (!isPaidPlan && totalFiles >= 2) {
      setUpgradeFeature("Uploading more than 2 documents");
      setShowUpgradeDialog(true);
      return Upload.LIST_IGNORE;
    }

    // Check file size for free plan: max 450KB
    if (!isPaidPlan && file.size > 450 * 1024) {
      setUpgradeFeature("Uploading files larger than 450KB");
      setShowUpgradeDialog(true);
      return Upload.LIST_IGNORE;
    }

    if (file.type !== "application/pdf") {
      message.error("Please upload a PDF file!");
      return Upload.LIST_IGNORE;
    }

    const isAlreadyAdded = fileList.some(
      (existingFile) =>
        existingFile.name === file.name && existingFile.size === file.size
    );
    if (isAlreadyAdded) {
      message.warning(`${file.name} is already selected!`);
      return Upload.LIST_IGNORE;
    }

    // Assign a unique ID if not present, but preserve the original File object
    if (!file.uid) {
      file.uid = `${file.name}-${Date.now()}`;
    }
    setFileList((prev) => [...prev, file]);
    message.success(`${file.name} added successfully!`);
    return false; // Prevent automatic upload
  };

  const handleRemove = (file) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    message.info(`${file.name} removed.`);
  };

  const closePdfViewer = () => {
    setIsPdfViewerOpen(false);
    setPreviewPdfUrl("");
    setPreviewPdfTitle("");
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = "";
    }
  };

  const isPdfItem = (item, isUrl) => {
    if (!item) return false;
    if (isUrl) return /\.pdf(?:$|[?#])/i.test(item);
    return (item?.type || "").toLowerCase() === "application/pdf" || /\.pdf$/i.test(item?.name || "");
  };

  const handlePreviewPdf = (item, isUrl) => {
    if (!isPdfItem(item, isUrl)) return;

    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = "";
    }

    if (isUrl) {
      setPreviewPdfUrl(item);
      setPreviewPdfTitle(item);
    } else {
      const objectUrl = URL.createObjectURL(item);
      previewObjectUrlRef.current = objectUrl;
      setPreviewPdfUrl(objectUrl);
      setPreviewPdfTitle(item?.name || "PDF Preview");
    }

    setIsPdfViewerOpen(true);
  };

  const addUrl = () => {
    // Check if user is on free plan and trying to select more than allowed
    if (!isPaidPlan) {
      const remaining = Math.max(0, 2 - totalFiles);
      const totalSelections = fileList.length + urls.length;
      if (totalSelections >= remaining) {
        setUpgradeFeature("Uploading more than 2 documents");
        setShowUpgradeDialog(true);
        return;
      }
    }

    // Check if user is on free plan and already has 2 or more documents
    if (!isPaidPlan && totalFiles >= 2) {
      setUpgradeFeature("Uploading more than 2 documents");
      setShowUpgradeDialog(true);
      return;
    }

    const trimmedUrl = currentUrl.trim();
    if (!trimmedUrl) {
      message.warning("Please enter a URL!");
      return;
    }

    // Basic URL validation
    try {
      new URL(trimmedUrl);
    } catch (error) {
      message.error("Please enter a valid URL (e.g., https://example.com)!");
      return;
    }

    if (urls.includes(trimmedUrl)) {
      message.warning("This URL is already added!");
      return;
    }

    setUrls((prev) => [...prev, trimmedUrl]);
    setCurrentUrl("");
    message.success("URL added successfully!");
  };

  const draggerProps = {
    name: "file",
    multiple: isPaidPlan,
    beforeUpload: handleBeforeUpload,
    fileList, // Use the actual fileList for proper drag-and-drop handling
    showUploadList: false, // Manage list rendering manually
    accept: ".pdf",
    customRequest: ({ onSuccess }) => {
      onSuccess("ok"); // Dummy success callback
    },
  };
  const renderUploader = () => {
    if (isMobile) {
      return (
        <Upload {...draggerProps} className="upload-simple">
          <div className="upload-simple__inner">
            <Space size={12} align="center">
              <div className="upload-simple__icon">
                <InboxOutlined style={{ fontSize: 28, color: "#1890ff" }} />
              </div>
              <div>
                <Text strong>Select PDF files</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Tap to browse your device
                </Text>
              </div>
            </Space>
            <Button type="primary" icon={<UploadOutlined />}>
              Browse
            </Button>
          </div>
        </Upload>
      );
    }

    return (
      <Dragger {...draggerProps} style={draggerStyle} className="upload-dragger">
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: "16px" }}>
          Drop or click to select your files {!isPaidPlan ? " (Max 450KB)" : ""}
        </p>
      </Dragger>
    );
  };

  const uploadFilesAndProcess = async () => {
    if (!hasSelections) {
      message.warning("Please add at least one file or URL to proceed!");
      return;
    }

    const balance = await checkBalance();
    if (balance < 3) {
      message.error("You don't have enough credits. Please recharge your account.");
      return;
    }
    setProcessing(true);
    const success = await startProcessing(fileList, urls);
    if (success) {
      message.success("Files uploaded successfully!");
    } else {
      message.error("File upload failed");
      setProcessing(false);
    }
  };

  const getStepIcon = (step) => {
    const stepStatus = status.stepStatuses[step];
    if (stepStatus === "Completed") return <CheckOutlined style={{ color: "#52c41a" }} />;
    if (stepStatus === "In Progress") return <LoadingOutlined style={{ color: "#1890ff" }} />;
    return null;
  };

  const handleFinish = () => {
    setProcessing(false);
    setCompleted(false);
  };

  // Calculate remaining files for display
  const getLimitInfo = () => {
    if (isPaidPlan) {
      return null; // Don't show card for paid users
    }

    const remaining = Math.max(0, 2 - totalFiles);
    if (remaining > 1) {
      return {
        message: `${remaining} files remaining to upload`,
        canUpload: true,
        showUpgrade: true,
      };
    } else if (remaining === 1) {
      return {
        message: `${remaining} file remaining to upload`,
        canUpload: true,
        showUpgrade: true,
      };
    } else {
      return {
        message: "Upload file limit reached: You have used all 2 free uploads.",
        canUpload: false,
        showUpgrade: true,
      };
    }
  };

  const limitInfo = getLimitInfo();

  return (
    <>
      <UpgradeDialog
        visible={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature={upgradeFeature}
      />
      <div
        style={{
          height: "70vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Upload Limit Info Card */}
        {limitInfo && !processing && (
          <div
            size="small"
            style={{
              marginBottom: 16,
              borderRadius: 8,
              padding: 16,
              background: limitInfo.canUpload ? '#f6ffed' : '#fff2f0',
              border: `2px solid ${limitInfo.canUpload ? '#b7eb8f' : '#ffccc7'}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {limitInfo.canUpload ? (
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                ) : (
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                )}
                <Text style={{ fontSize: 16, fontWeight: 500 }}>
                  {limitInfo.message}
                </Text>
              </div>
              {limitInfo.showUpgrade && (
                <Button
                  type="primary"
                  size="default"
                  onClick={() => {
                    setUpgradeFeature("Uploading more than 2 documents");
                    setShowUpgradeDialog(true);
                  }}
                  style={{ fontSize: 14 }}
                >
                  Increase Limits
                </Button>
              )}
            </div>
          </div>
        )}

      {processing ? (
        <Card
          style={{
            width: isMobile ? "100%" : "35vw",
            maxWidth: isMobile ? "100%" : undefined,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
            borderRadius: "8px",
            margin: "0 auto",
            // height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <h2>Please take your time, we appreciate your patience</h2>
          <Steps direction="vertical" current={status.currentStep}>
            {Object.keys(status.stepStatuses).map((step, index) => (
              <Step key={index} title={step} icon={getStepIcon(step)} />
            ))}
          </Steps>
          <Progress
            percent={Math.round(
              (status.currentStep / Object.keys(status.stepStatuses).length) * 100
            )}
          />
          {completed && (
            <Button type="primary" onClick={handleFinish} style={{ marginTop: 20 }}>
              Finish
            </Button>
          )}
        </Card>
      ) : (
        <Row gutter={rowGutter} style={{ height: isMobile ? "auto" : "100%" }}>
          {hasSelections ? (
            <>
              <Col xs={24} md={12} style={{ height: isMobile ? "auto" : "100%" }}>
                <Card
                  style={{ height: isMobile ? "auto" : "100%" }}
                  bodyStyle={{
                    height: isMobile ? "auto" : "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: isMobile ? "flex-start" : "center",
                    padding: "0",
                  }}
                >
                  <div
                    style={{
                      padding: isMobile ? "12px" : "16px",
                      height: isMobile ? "auto" : "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: isMobile ? "flex-start" : "center",
                      gap: 12,
                    }}
                  >
                    {renderUploader()}
                    <div style={{ marginTop: 16, display: "flex", gap: "8px" }}>
                      <Input
                        placeholder="Enter URL (e.g., https://example.com)"
                        value={currentUrl}
                        onChange={(e) => setCurrentUrl(e.target.value)}
                        onPressEnter={addUrl}
                        style={{ flex: 1 }}
                      />
                      <Button onClick={addUrl} style={{ flexShrink: 0 }}>
                        Add
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12} style={{ height: isMobile ? "auto" : "100%" }}>
                <Card
                  style={{ height: isMobile ? "auto" : "100%" }}
                  bodyStyle={{
                    height: isMobile ? "auto" : "100%",
                    display: "flex",
                    flexDirection: "column",
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      flex: isMobile ? "none" : 1,
                      minHeight: 0,
                      overflowY: isMobile ? "visible" : "auto",
                      padding: "16px 16px 12px 16px",
                    }}
                  >
                    <List
                      style={{ padding: 0 }}
                      dataSource={[...fileList, ...urls].filter((item) => !!item)}
                      renderItem={(item) => {
                        const isUrl = typeof item === "string";
                        const displayName = isUrl ? item : (item.name || "Unknown file");

                        return (
                          <List.Item
                            key={isUrl ? item : item.uid || `${item.name}-${Date.now()}`}
                            style={{ padding: "8px 0" }}
                            actions={[
                              isPdfItem(item, isUrl) ? (
                                <Button
                                  key="view"
                                  type="text"
                                  icon={<EyeOutlined style={{ color: "#1677ff" }} />}
                                  onClick={() => handlePreviewPdf(item, isUrl)}
                                />
                              ) : null,
                              <Button
                                key="delete"
                                type="text"
                                icon={<DeleteOutlined style={{ color: "red" }} />}
                                onClick={() => {
                                  if (isUrl) {
                                    setUrls(urls.filter((url) => url !== item));
                                    message.info("URL removed.");
                                  } else {
                                    handleRemove(item);
                                  }
                                }}
                              />,
                            ]}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                              {isUrl ? (
                                <GlobalOutlined style={{ color: "#1890ff", fontSize: 20 }} />
                              ) : (
                                <FileOutlined style={{ color: "#1890ff", fontSize: 20 }} />
                              )}
                              <Tooltip title={displayName}>
                                <Text
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {displayName}
                                </Text>
                              </Tooltip>
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  </div>
                  <div style={{ padding: "12px 16px 16px 16px", marginTop: "auto", textAlign: "right" }}>
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      onClick={uploadFilesAndProcess}
                      style={{ width: "100%" }}
                    >
                      Upload and Process Files
                    </Button>
                  </div>
                </Card>
              </Col>
            </>
          ) : (
            <Col
              span={24}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", // Horizontal center
                alignItems: "center", // Vertical center
                // minHeight: "100vh", // Full screen height for centering
                boxShadow: "none",
              }}
            >
              <Card
                style={{
                  width: isMobile ? "100%" : "50%",
                  maxWidth: isMobile ? "100%" : "600px",
                  height: isMobile ? "280px" : "400px", // Fixed height
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                bodyStyle={{
                  padding: "0",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div style={{ padding: "16px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  {renderUploader()}
                  <div style={{ marginTop: 16, display: "flex", gap: "8px" }}>
                    <Input
                      placeholder="Enter URL (e.g., https://example.com)"
                      value={currentUrl}
                      onChange={(e) => setCurrentUrl(e.target.value)}
                      onPressEnter={addUrl}
                      style={{ flex: 1 }}
                    />
                    <Button onClick={addUrl} style={{ flexShrink: 0 }}>
                      Add
                    </Button>
                  </div>
                </div>
              </Card>
            </Col>
          )}
        </Row>
      )}
      <Modal
        open={isPdfViewerOpen}
        title={previewPdfTitle || "PDF Preview"}
        onCancel={closePdfViewer}
        footer={null}
        width="min(95vw, 1200px)"
        centered
        destroyOnClose
      >
        {previewPdfUrl ? (
          <div style={{ height: isMobile ? "65vh" : "75vh" }}>
            <PdfViewer
              pdfUrls={[{ url: previewPdfUrl, title: previewPdfTitle || "PDF Preview" }]}
              noBorder
            />
          </div>
        ) : null}
      </Modal>
      <style jsx>{`
        .upload-dragger:hover {
          border-color: #1890ff !important;
          background: #fafafa !important;
        }
        .upload-dragger .ant-upload {
          background: #fafafa !important;
        }
        .upload-dragger .ant-upload:hover {
          background: #fafafa !important;
          border-color: #1890ff !important;
        }
        .upload-dragger .ant-upload-drag:hover {
          background: #fafafa !important;
          border-color: #1890ff !important;
        }
        .upload-dragger .ant-upload-drag-hover {
          background: #fafafa !important;
          border-color: #1890ff !important;
        }
        .upload-dragger .ant-upload-drag.ant-upload-drag-hover {
          background: #fafafa !important;
          border-color: #1890ff !important;
        }
        .upload-simple {
          width: 100%;
          display: block;
        }
        .upload-simple .ant-upload {
          width: 100%;
        }
        .upload-simple__inner {
          width: 100%;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          padding: 12px;
          background: #fafafa;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .upload-simple__icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #e6f4ff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
    </>
  );
}

export default FileUploadPanel;
